import React, { createContext, useContext, useState, useEffect } from "react";
import {
  sendOTP,
  loginWithOTP,
  generateAnonymousId,
  guestLogin,
} from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to check if user is truly authenticated
  // User is authenticated only when token is present AND phoneNumber != "Guest"
  const checkIsAuthenticated = (authToken, phoneNum) => {
    return !!(authToken && phoneNum && phoneNum !== "Guest");
  };

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("userData");
    const storedPhone = localStorage.getItem("phoneNumber");

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(checkIsAuthenticated(storedToken, storedPhone));
    }
    setLoading(false);
  }, []);

  // Send OTP via WhatsApp or SMS
  const sendWhatsAppOTP = async (phoneNumber) => {
    return await sendOTP(phoneNumber);
  };

  // Login with phone number and OTP
  const login = async (phoneNumber, otp) => {
    const result = await loginWithOTP(phoneNumber, otp);

    if (result.success) {
      const userData = result.data;

      // Store in localStorage
      localStorage.setItem("authToken", userData.token);
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("phoneNumber", phoneNumber);

      // Update state
      setToken(userData.token);
      setUser(userData);
      setIsAuthenticated(checkIsAuthenticated(userData.token, phoneNumber));

      return { success: true, data: userData };
    }

    return result;
  };

  // Guest login via /api/user/guest endpoint
  const loginAsGuest = async () => {
    const result = await guestLogin();

    if (result.success) {
      const anonymousId = result.anonymousId || generateAnonymousId();
      const userData = {
        ...result.data,
        isGuest: true,
        anonymousId: anonymousId,
      };

      // Store in localStorage
      localStorage.setItem("authToken", userData.token);
      localStorage.setItem("userData", JSON.stringify(userData));
      // When login as a guest, set phoneNumber always to "Guest"
      localStorage.setItem("phoneNumber", "Guest");

      // Update state
      setToken(userData.token);
      setUser(userData);
      setIsAuthenticated(checkIsAuthenticated(userData.token, "Guest"));

      return { success: true, data: userData };
    }

    return result;
  };

  // Anonymous login (Skip for now)
  const loginAnonymously = async () => {
    const anonymousId = generateAnonymousId();

    // Try to login with the anonymous ID (using same API)
    const result = await loginWithOTP(anonymousId, "123456");

    if (result.success) {
      const userData = {
        ...result.data,
        isAnonymous: true,
        anonymousId: anonymousId,
      };

      // Store in localStorage
      localStorage.setItem("authToken", userData.token);
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("phoneNumber", anonymousId);

      // Update state
      setToken(userData.token);
      setUser(userData);
      setIsAuthenticated(checkIsAuthenticated(userData.token, anonymousId));

      return { success: true, data: userData };
    } else {
      // If API fails, create a local anonymous session
      const localAnonymousUser = {
        ID: Date.now(),
        name: "Guest",
        last: "",
        tag: "Guest",
        token: `anonymous_${anonymousId}_${Date.now()}`,
        isAnonymous: true,
        anonymousId: anonymousId,
      };

      localStorage.setItem("authToken", localAnonymousUser.token);
      localStorage.setItem("userData", JSON.stringify(localAnonymousUser));
      localStorage.setItem("phoneNumber", "Guest");

      setToken(localAnonymousUser.token);
      setUser(localAnonymousUser);
      setIsAuthenticated(checkIsAuthenticated(localAnonymousUser.token, "Guest"));

      return { success: true, data: localAnonymousUser };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("phoneNumber");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        loading,
        sendWhatsAppOTP,
        login,
        loginAnonymously,
        loginAsGuest,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
